// 운영자 스크립트(`packages/core/scripts/org-plan.mjs`)의 인자 해석·검증·다음 값 계산 — node 가 타입만 지워 바로 읽으므로 타입 지우기로 안 되는 문법(enum·namespace)을 쓰지 않는다.

import { kstMinute } from "../../lib/kst-time";
import { isUuid } from "../../lib/uuid";
import { DEFAULT_PLAN_CODE, isPlanCode, PLAN_CODES, type PlanCode } from "./types";

// org_plan.active_site_limit 은 integer 열이다.
export const MAX_SITE_LIMIT = 2147483647;

export const ORG_PLAN_USAGE = [
  "사용법:",
  "  tools/scripts/op-run.sh -- node packages/core/scripts/org-plan.mjs <조직 slug|id> --show",
  `  tools/scripts/op-run.sh -- node packages/core/scripts/org-plan.mjs <조직 slug|id> <${PLAN_CODES.join("|")}> [--limit N|none] [--grace-until YYYY-MM-DD|ISO|none] [--dry-run]`,
  "  --limit·--grace-until 을 빼면 지금 값을 그대로 둔다(행이 없으면 없음) · invited 는 한도가 늘 없음",
  "  invited(또는 행 없음)에서 다른 플랜으로 바꿀 때는 --limit 을 꼭 준다(무제한이면 none)",
  "  --grace-until 날짜만 주면 그날 한국시간 자정까지(다음날 00:00 KST) · 시각을 주면 그 순간까지",
].join("\n");

export type OrgRef = { by: "id" | "slug"; value: string };

// undefined = 인자 없음(지금 값 유지) · null = none(비움).
export type OrgPlanCommand =
  | { kind: "show"; org: OrgRef }
  | { kind: "set"; org: OrgRef; plan: PlanCode; limit: number | null | undefined; graceUntil: string | null | undefined; dryRun: boolean };

export type ParseResult = { ok: true; command: OrgPlanCommand } | { ok: false; error: string };

// DB 에 쓰는 세 열 — 표에서 읽은 행도 이 모양으로 본다(plan 은 검사 전 문자열).
export interface OrgPlanValues {
  plan: string;
  active_site_limit: number | null;
  grace_until: string | null;
}

export const parseLimit = (raw: string): { ok: true; value: number | null } | { ok: false; error: string } => {
  if (raw === "none") return { ok: true, value: null };
  if (/^-\d+$/.test(raw)) return { ok: false, error: `--limit 은 음수가 될 수 없습니다: ${raw}` };
  if (!/^\d+$/.test(raw)) return { ok: false, error: `--limit 은 0 이상의 정수 또는 none 입니다: ${raw}` };
  const value = Number(raw);
  if (value > MAX_SITE_LIMIT) return { ok: false, error: `--limit 이 너무 큽니다(최대 ${MAX_SITE_LIMIT}): ${raw}` };
  return { ok: true, value };
};

const GRACE_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(?:Z|[+-](\d{2}):(\d{2})))?$/;

// 날짜만이면 그날 KST 끝(다음날 00:00 KST = 그날 15:00Z) · 시각 포함 ISO 는 그 순간 · 넘치는 날짜·시각(2026-02-30·T24:00)은 거절.
export const parseGraceUntil = (raw: string): { ok: true; value: string | null } | { ok: false; error: string } => {
  if (raw === "none") return { ok: true, value: null };
  const fail = { ok: false as const, error: `--grace-until 은 YYYY-MM-DD(그날 한국시간 자정까지) · YYYY-MM-DDTHH:MM(:SS)(Z|±HH:MM) · none 입니다: ${raw}` };
  const m = GRACE_ISO.exec(raw);
  if (!m) return fail;
  const [, y, mo, d, hh, mi, ss, oh, om] = m;
  const days = new Date(Date.UTC(Number(y), Number(mo), 0)).getUTCDate();
  const inRange = (v: string | undefined, max: number, min = 0) => v === undefined || (Number(v) >= min && Number(v) <= max);
  if (!inRange(mo, 12, 1) || !inRange(d, days, 1) || !inRange(hh, 23) || !inRange(mi, 59) || !inRange(ss, 59) || !inRange(oh, 23) || !inRange(om, 59)) return fail;
  const at = Date.parse(hh === undefined ? `${raw}T15:00:00Z` : raw);
  if (!Number.isFinite(at)) return fail;
  return { ok: true, value: new Date(at).toISOString() };
};

export const orgRefOf = (raw: string): OrgRef => (isUuid(raw) ? { by: "id", value: raw.toLowerCase() } : { by: "slug", value: raw });

export const parseOrgPlanArgs = (argv: readonly string[]): ParseResult => {
  const positional: string[] = [];
  const flags = new Map<string, string | true>();
  const VALUE_FLAGS = new Set(["--limit", "--grace-until"]);
  const BOOL_FLAGS = new Set(["--show", "--dry-run"]);
  for (let i = 0; i < argv.length; i += 1) {
    const arg = argv[i];
    if (!arg.startsWith("--")) {
      positional.push(arg);
      continue;
    }
    const eq = arg.indexOf("=");
    const name = eq === -1 ? arg : arg.slice(0, eq);
    if (flags.has(name)) return { ok: false, error: `${name} 가 두 번 왔습니다` };
    if (BOOL_FLAGS.has(name)) {
      if (eq !== -1) return { ok: false, error: `${name} 는 값을 받지 않습니다` };
      flags.set(name, true);
    } else if (VALUE_FLAGS.has(name)) {
      const value = eq === -1 ? argv[i + 1] : arg.slice(eq + 1);
      if (value === undefined || value === "" || (eq === -1 && value.startsWith("--"))) return { ok: false, error: `${name} 에 값이 없습니다` };
      if (eq === -1) i += 1;
      flags.set(name, value);
    } else {
      return { ok: false, error: `모르는 인자: ${arg}` };
    }
  }

  const [orgRaw, planRaw, ...rest] = positional;
  if (!orgRaw) return { ok: false, error: "조직(slug 또는 id)이 없습니다" };
  if (rest.length > 0) return { ok: false, error: `남는 인자: ${rest.join(" ")}` };
  const org = orgRefOf(orgRaw);

  if (flags.has("--show")) {
    if (planRaw !== undefined || flags.size > 1) return { ok: false, error: "--show 는 조직 말고 다른 인자와 같이 쓰지 않습니다" };
    return { ok: true, command: { kind: "show", org } };
  }

  if (planRaw === undefined) return { ok: false, error: `플랜이 없습니다 (${PLAN_CODES.join("|")})` };
  if (!isPlanCode(planRaw)) return { ok: false, error: `모르는 플랜: ${planRaw} (${PLAN_CODES.join("|")})` };

  let limit: number | null | undefined;
  const limitRaw = flags.get("--limit");
  if (typeof limitRaw === "string") {
    const parsed = parseLimit(limitRaw);
    if (!parsed.ok) return parsed;
    limit = parsed.value;
  }
  // DB check org_plan_invited_unlimited 와 같다 — 초대에 숫자 한도를 적으면 조용히 버리지 않고 거절한다.
  if (planRaw === "invited" && typeof limit === "number") return { ok: false, error: "invited 는 한도가 없습니다 — --limit 을 빼거나 none" };

  let graceUntil: string | null | undefined;
  const graceRaw = flags.get("--grace-until");
  if (typeof graceRaw === "string") {
    const parsed = parseGraceUntil(graceRaw);
    if (!parsed.ok) return parsed;
    graceUntil = parsed.value;
  }

  return { ok: true, command: { kind: "set", org, plan: planRaw, limit, graceUntil, dryRun: flags.has("--dry-run") } };
};

// 바꾼 뒤 값 — 인자가 없던 열은 지금 값 · invited 는 한도를 null 로 강제 · invited(행 없음 포함)에서 다른 플랜으로 가며 --limit 이 없으면 무제한으로 새지 않게 거절.
export const nextOrgPlan = (
  current: OrgPlanValues | null,
  command: Extract<OrgPlanCommand, { kind: "set" }>
): { ok: true; value: OrgPlanValues } | { ok: false; error: string } => {
  const fromInvited = current === null || current.plan === "invited";
  if (command.plan !== "invited" && command.limit === undefined && fromInvited)
    return { ok: false, error: `초대 플랜에서 바꿀 때는 --limit 을 주세요 — 지금이 ${current ? "invited" : "행 없음"}이라 한도가 없습니다(한도 없음은 --limit none)` };
  return {
    ok: true,
    value: {
      plan: command.plan,
      active_site_limit: command.plan === "invited" ? null : command.limit !== undefined ? command.limit : (current?.active_site_limit ?? null),
      grace_until: command.graceUntil !== undefined ? command.graceUntil : (current?.grace_until ?? null),
    },
  };
};

// 전/후 한 줄 — 유예 끝은 KST `YYYY-MM-DD HH:mm` · 행이 없으면 앱이 초대(무제한)로 본다는 것까지 적는다.
export const describeOrgPlan = (row: OrgPlanValues | null): string =>
  row
    ? `plan=${row.plan} · limit=${row.active_site_limit ?? "없음"} · grace_until=${row.grace_until ? kstMinute(row.grace_until) : "없음"}`
    : `행 없음(앱은 ${DEFAULT_PLAN_CODE} · 한도 없음으로 본다)`;

export const sameOrgPlan = (a: OrgPlanValues | null, b: OrgPlanValues): boolean =>
  a !== null &&
  a.plan === b.plan &&
  a.active_site_limit === b.active_site_limit &&
  (a.grace_until === null || b.grace_until === null ? a.grace_until === b.grace_until : Date.parse(a.grace_until) === Date.parse(b.grace_until));
